import { useEffect, useMemo, useState, useRef, useCallback } from "react";

import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { afterPendingPasswordChange } from "@/pages/Private/redux/account/account.api";
import { useProductAnalytics, useTrackFeature } from "@/utils/productAnalytics/useProductAnalytics";
import { contactSelector } from "@/pages/Private/redux/contact/contact.slice";
import { tokenAdoptionDecision } from "@/utils/auth/tokenAdoption";

import { Logout } from "@/pages/Logout/Logout";
import { Private } from "@/pages/Private/Private";
import { authSelector, setAuth } from "@/pages/Public/redux/auth.slice";
import { useLazyFetchMeQuery } from "@/pages/Public/redux/auth.api";
import { ErrorBoundary, LoadingOverlay } from "@/components";

import { Public } from "@/pages/Public/Public";

import { useIsFeatureActiveUnauthenticated } from "@/utils/useIsFeatureActiveUnauthenticated";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { ROUTE_CONFIG } from "./config";

const { LOGIN, REGISTER, LOGOUT, APPLICATION_STATUS } = ROUTE_CONFIG;

export const Routing = () => {
	const { pathname } = useLocation();

	useProductAnalytics(pathname);
	const trackFeature = useTrackFeature();
	const openProspectId = useAppSelector((state) => {
		const { isLeadPanelOpen, leadPanelProspectId } = contactSelector(state);

		return isLeadPanelOpen ? leadPanelProspectId : null;
	});

	// Observe web panel opens here: the shared panel also runs in the extension.
	// Detail refreshes leave this scalar unchanged and must not record another view.
	useEffect(() => {
		if (openProspectId !== null) {
			trackFeature("lead-panel", "view");
		}
	}, [openProspectId, trackFeature]);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { token, user } = useAppSelector(authSelector);

	const [fetchMe] = useLazyFetchMeQuery();

	const SELF_REGISTER = useIsFeatureActiveUnauthenticated(FeaturesCodes.SELF_REGISTER);

	const onLogoutRoute = pathname === LOGOUT;

	const [isLoading, setIsLoading] = useState(!onLogoutRoute);

	const fallbackRoute = useMemo(() => {
		if (SELF_REGISTER === true) {
			return REGISTER;
		} else if (SELF_REGISTER === false) {
			return LOGIN;
		}

		return "";
	}, [SELF_REGISTER]);

	useEffect(() => {
		if (token && !user && isLoading) {
			fetchMe(token)
				.then((res) => {
					if (res.status === "rejected") {
						afterPendingPasswordChange(() => {
							const storedToken = localStorage.getItem("token");
							const decision = tokenAdoptionDecision(token, storedToken);

							if (decision === "adopt") {
								dispatch(setAuth({ token: storedToken }));

								return;
							}

							if (decision === "reload") {
								window.location.reload();

								return;
							}

							navigate(LOGOUT);
						});
					}
				})
				.catch((e) => {
					console.error(e.status);
				});
		}
	}, [token, user, isLoading, fetchMe, navigate, dispatch]);

	useEffect(() => {
		if ((!token || !!user) && isLoading) {
			setTimeout(() => setIsLoading(false), 1000);
		}
	}, [token, user, isLoading]);

	const getFallbackRoute = () => {
		if (user) {
			return "";
		} else {
			return fallbackRoute;
		}
	};

	return isLoading ? (
		<LoadingOverlay />
	) : (
		<ErrorBoundary>
			<Routes>
				<Route element={<Logout />} path={LOGOUT} />
				<Route element={<Private />} path="/app/*" />
				{SELF_REGISTER !== undefined && <Route element={<Navigate to={getFallbackRoute()} />} />}
				<Route element={<Public />} path="*" />
			</Routes>
		</ErrorBoundary>
	);
};
